import { AddRounded } from '@mui/icons-material'
import { Box, Button, Chip, Modal, Stack, Switch, Tab, Tabs, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridPaginationMeta, GridPaginationModel, GridRowSelectionModel, GridToolbar } from '@mui/x-data-grid'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import { useAppSelector } from '../../store/Hooks'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import RoutingManager from '../routing/Routing'
import CreateNewFlowModal from './CreateNewFlowModal'
import FlowsPrevRuns from './FlowsPrevRuns'
import FlowTemplates from './FlowTemplates'
import RunModal from './multi_run/RunModal'

const PAGE_SIZE = 50

const columns: GridColDef<any>[] = [
    { field: 'name', headerName: 'Contract name', width: 400, renderCell: params => params.row.name?.replace(/^Settlement Engine of\s*/i, '')?.replace(/\sv\d+$/i, '')?.trim() },
    { field: 'engineName', headerName: 'Engine', width: 400, renderCell: params => params.row.engineName },
    { field: 'engineVersion', headerName: 'Engine version', renderCell: params => params.row?.engineVersion ? `v${params.row.engineVersion}` : '', width: 130 },
    { field: 'productType', headerName: 'Product Type', renderCell: params => params.row?.productType ? <Chip label={params.row.productType} color='secondary' size='small' /> : null },
    { field: 'externalId', headerName: 'Business key', width: 130 },
    { field: 'createdAt', headerName: 'Created at', valueGetter: (_, row) => new Date(row.createdAt).toLocaleDateString() }
]
const slots: any = { toolbar: GridToolbar }
const slotProps: any = {
    toolbar: {
        showQuickFilter: true,
        quickFilterProps: {
            variant: 'outlined',
            size: 'small'
        }
    },
    loadingOverlay: {
        variant: 'skeleton',
        noRowsVariant: 'skeleton'
    }
}
const sx = {
    '&.MuiDataGrid-root': {
        padding: 3,
        borderRadius: 2.5
    }, '& .MuiDataGrid-cell:focus': {
        outline: 'none'
    },
    '& .MuiDataGrid-cell:focus-within': {
        outline: 'none'
    }
}

export default function FlowsPage() {
    const params = RoutingManager.getParams<{ selectedTab: 'contracts' | 'prevRuns' | 'engines' }>(useAppSelector(s => s.session.routeParams))
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: PAGE_SIZE })
    const [rowCountState, setRowCountState] = useState(0)
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([])
    const [selectedTab, setSelectedTab] = useState<'contracts' | 'prevRuns' | 'engines'>(params?.selectedTab ?? 'contracts')
    const [searchParam, setSearchParam] = useState(undefined)
    const [openRunModal, setOpenRunModal] = useState(false)
    const [openCreateModal, setOpenCreateModal] = useState(false)
    const [blockUI, setBlockUI] = useState(false)
    const [showPendingTemplates, setShowPendingTemplates] = useState(false)
    const [runMode, setRunMode] = useState<'full' | 'dry'>(undefined)
    const paginationMetaRef = useRef<GridPaginationMeta>(undefined)
    const mapPageToNextCursor = useRef<{ [page: number]: string | undefined }>({})

    const { status, response, refetch } = useQuery({
        queryKey: buildQueryKey(`${searchParam ? `flows-${searchParam}` : 'flows'}`, paginationModel.page, mapPageToNextCursor.current[paginationModel.page - 1] ?? 'first'),
        queryFn: () => FlowApiClient.getFlows({ batchSize: PAGE_SIZE, nextToken: mapPageToNextCursor.current[paginationModel.page - 1], searchParam: searchParam }),
        cache: false
    })

    const paginationMeta = useMemo(() => {
        if (response?.hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== response?.hasNextPage) {
            paginationMetaRef.current = { hasNextPage: response?.hasNextPage }
        }
        return paginationMetaRef.current
    }, [response?.hasNextPage])

    const rows = useMemo(() => response?.items ?? [], [response?.items])

    useEffect(() => {
        if (!(status === 'loading') && response?.nextToken) {
            mapPageToNextCursor.current[paginationModel.page] = response?.nextToken
        }
    }, [paginationModel.page, status === 'loading', response?.nextToken])

    useEffect(() => {
        if (!paginationMeta?.hasNextPage) {
            setRowCountState(paginationModel.page * PAGE_SIZE + rows.length)
        } else {
            setRowCountState(-1)
        }
    }, [paginationMeta?.hasNextPage, paginationModel.page])

    useEffect(() => {
        if (selectedTab === 'prevRuns') {
            setSelectedRows([])
        }
    }, [selectedTab])

    const handlePaginationModelChange = useCallback((newPaginationModel: GridPaginationModel) => {
        if (newPaginationModel.page === 0 || mapPageToNextCursor.current[newPaginationModel.page - 1]) {
            setPaginationModel(newPaginationModel)
        }
    }, [])

    const handleRowSelectionModelChange = useCallback(
        (newSelection: GridRowSelectionModel) => {
            setSelectedRows(newSelection)
        }, [])

    const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: 'contracts' | 'prevRuns' | 'engines') => {
        setSelectedTab(newValue)
    }, [])

    const handleCloseRunModal = useCallback(() => {
        if (blockUI) return
        setOpenRunModal(false)
    }, [blockUI])

    const handleCloseCreateModal = useCallback(() => {
        setOpenCreateModal(false)
    }, [])

    const handleOpenRunModal = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        const mode = event.currentTarget.dataset.mode as 'full' | 'dry'
        setRunMode(mode)
        setOpenRunModal(true)
    }, [])

    const handleOpenCreateModal = useCallback(() => {
        setOpenCreateModal(true)
    }, [])

    const allSelectedDiscendents = useMemo(() => {
        if (!rows?.length || selectedRows.length === 0) return false
        const selected = rows.filter(r => selectedRows.includes(r.uuid))
        return selected.every(r => r?.settlementEngine?.ancestorKey && r?.settlementEngine?.ancestorKey === selected?.[0]?.settlementEngine?.ancestorKey)
    }, [selectedRows])

    const selectedAncestor = useMemo(() => {
        const found = rows.find(r => selectedRows.includes(r.uuid))
        if (!found) return null
        return {
            ancestorUUID: found?.settlementEngine?.ancestorKey,
            ancestorName: found?.settlementEngine?.ancestorName
        }
    }, [rows, selectedRows])

    return (
        <>
            <Stack spacing={3} sx={{ width: '100%', height: '100%' }}>
                <Typography variant='h5' fontWeight={700}>Settlements</Typography>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
                        <Tabs value={selectedTab} onChange={handleTabChange}>
                            <Tab label='Contracts' value='contracts' />
                            <Tab label='Previous Runs' value='prevRuns' />
                            <Tab label='Settlement Engines' value='engines' />
                        </Tabs>
                        {selectedTab === 'contracts' && (
                            <Stack flexDirection='row' alignItems='center' gap={1.5}>
                                <Typography variant='caption' fontWeight={selectedRows.length > 20 ? 700 : undefined} color={t => selectedRows.length > 20 ? t.palette.error.main : t.palette.text.secondary}>{selectedRows.length} / 20 selected</Typography>
                                <Button
                                    variant='contained'
                                    disabled={!allSelectedDiscendents || selectedRows.length > 20}
                                    onClick={handleOpenRunModal}>Run
                                </Button>
                            </Stack>
                        )}
                        {selectedTab === 'engines' && (
                            <Stack flexDirection='row' alignItems='center' gap={1.5}>
                                <Typography variant='body2'>Your pending items</Typography>
                                <Switch checked={showPendingTemplates} onChange={e => setShowPendingTemplates(e.target.checked)} />
                                <Button onClick={handleOpenCreateModal} variant='contained' startIcon={<AddRounded />}>Create new engine</Button>
                            </Stack>
                        )}
                    </Stack>
                </Box>
                {selectedTab === 'contracts'
                    ? (
                            <DataGrid
                                sx={sx}
                                loading={status === 'loading' || status === 'stale'}
                                getRowId={row => row.uuid}
                                columns={columns}
                                rows={rows}
                                paginationMode='server'
                                paginationMeta={paginationMeta}
                                paginationModel={paginationModel}
                                onPaginationModelChange={handlePaginationModelChange}
                                filterMode='server'
                                onFilterModelChange={m => setSearchParam(m.quickFilterValues[0])}
                                filterDebounceMs={500}
                                rowCount={rowCountState}
                                pageSizeOptions={[PAGE_SIZE]}
                                checkboxSelection
                                onRowSelectionModelChange={handleRowSelectionModelChange}
                                rowSelectionModel={selectedRows}
                                disableRowSelectionOnClick
                                keepNonExistentRowsSelected
                                slots={slots}
                                slotProps={slotProps}
                            />
                        )
                    : selectedTab === 'prevRuns'
                        ? <FlowsPrevRuns />
                        : <FlowTemplates showPendingTemplates={showPendingTemplates} />}
            </Stack>
            {openRunModal && (
                <Modal open={openRunModal} onClose={handleCloseRunModal} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    <Box>
                        <RunModal setOpenModal={setOpenRunModal} setBlockUI={setBlockUI} selectedRows={selectedRows as string[]} runMode={runMode} refetch={refetch} ancestor={selectedAncestor} />
                    </Box>
                </Modal>
            )}
            {openCreateModal && (
                <Modal open={openCreateModal} onClose={handleCloseCreateModal} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                    <Box>
                        <CreateNewFlowModal setOpenModal={setOpenCreateModal} />
                    </Box>
                </Modal>
            )}
        </>
    )
}
